import type { TableColumn } from './translation.types';
import { isEmptyValue } from './buildParamSegment';

type RowObject = Record<string, unknown>;

function objectMapToRows(input: RowObject): RowObject[] {
  const numericKeys = Object.keys(input).filter((k) => /^\d+$/.test(k));
  if (numericKeys.length === 0) return [];
  return numericKeys
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => input[k])
    .filter((v): v is RowObject => Boolean(v) && typeof v === 'object' && !Array.isArray(v));
}

export function pickRowArray(raw: unknown, depth = 0): RowObject[] {
  if (depth > 5) return [];
  if (Array.isArray(raw)) return raw as RowObject[];
  if (!raw || typeof raw !== 'object') return [];

  const obj = raw as RowObject;

  const directKeys = ['gradientRows', 'GradientProgram', 'tableRows', 'rows', 'data'];
  for (const key of directKeys) {
    const val = obj[key];
    if (Array.isArray(val)) return val as RowObject[];
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const rows = objectMapToRows(val as RowObject);
      if (rows.length > 0) return rows;
    }
  }

  const nestedKeys = ['value', 'parameters', 'payload', 'result', 'data'];
  for (const key of nestedKeys) {
    const nestedRaw = obj[key];
    if (nestedRaw && typeof nestedRaw === 'object') {
      const nestedRows = pickRowArray(nestedRaw, depth + 1);
      if (nestedRows.length > 0) return nestedRows;
    }
  }

  return objectMapToRows(obj);
}

export function pickGroupRows(raw: unknown): RowObject[] {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as RowObject;

    const candidates: unknown[] = [obj, obj.value, obj.parameters];
    for (const candidate of candidates) {
      const rows = pickRowArray(candidate);
      if (rows.length > 0) return rows;
      if (Array.isArray(candidate)) return candidate as RowObject[];
    }

    const valueObj = obj.value && typeof obj.value === 'object' && !Array.isArray(obj.value)
      ? (obj.value as RowObject)
      : null;
    const paramsObj = obj.parameters && typeof obj.parameters === 'object' && !Array.isArray(obj.parameters)
      ? (obj.parameters as RowObject)
      : null;

    if (valueObj || paramsObj) {
      // Keep all keys from both wrappers; value keys take precedence when duplicated.
      return [{ ...(paramsObj ?? {}), ...(valueObj ?? {}) }];
    }

    return [obj];
  }

  const rows = pickRowArray(raw);
  if (rows.length > 0) return rows;
  if (Array.isArray(raw)) return raw as RowObject[];
  if (raw && typeof raw === 'object') return [raw as RowObject];
  return [];
}

export function rowHasValue(row: RowObject): boolean {
  return Object.values(row).some((cell) => !isEmptyValue(cell));
}

export function normalizeTableColumns(
  cols: Array<{ id: string; header: string; required?: boolean }> = [],
): TableColumn[] {
  const used = new Set<string>();
  return cols.map((c, idx) => {
    const base = String(c.id || c.header || `col_${idx + 1}`).trim() || `col_${idx + 1}`;
    let key = base;
    let n = 2;
    while (used.has(key)) {
      key = `${base}_${n}`;
      n += 1;
    }
    used.add(key);
    return {
      key,
      label: String(c.header || c.id || `Column ${idx + 1}`),
    };
  });
}
